// Pure scenario engine. Takes the real transaction list and a set of
// scenarios; returns a synthetic transaction list with the enabled
// scenarios' deltas applied. The result is fed straight into
// buildProjection() so forward-looking overlays are just normal projections
// on doctored input.
//
// Delta shapes:
//   { kind: "removeRecurring", seriesKey }
//   { kind: "scaleRecurring",  seriesKey, multiplier, startDate? }
//   { kind: "addRecurring",    desc, amount, type, recurrenceType, startDate? }
//
// seriesKey is the shared recurring identity (description + type).
import { seriesKeyOf, seriesKeyAliases } from './series-key.js';
import { previousOccurrence } from './planned.js';

export { seriesKeyOf };

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00`);
  }
  return new Date(value);
}

// Per-scenario application of the list-doctoring deltas: removeRecurring and
// addRecurring. scaleRecurring is intentionally NOT handled here — scaling is a
// forward-looking modifier on PROJECTED occurrences, so it's applied at
// projection time (see scaleDeltasOf + buildProjection's `scaleDeltas` option).
// Real transaction lists only contain past instances, so doctoring historical
// amounts can't express "scale from date X forward" the way the projection can.
//
// Exported so the editor can preview a single scenario's effect.
export function applyScenarioDeltas(transactions, scenario) {
  if (!scenario || !Array.isArray(scenario.deltas) || scenario.deltas.length === 0) {
    return transactions;
  }

  const removeKeys = new Set();
  const adds = [];   // [{desc, amount, type, recurrenceType, startDate}]

  scenario.deltas.forEach((d) => {
    if (!d || !d.kind) return;
    if (d.kind === 'removeRecurring' && d.seriesKey) removeKeys.add(d.seriesKey);
    else if (d.kind === 'addRecurring' && d.desc && Number.isFinite(Number(d.amount))) {
      adds.push({
        desc: d.desc,
        amount: Number(d.amount),
        type: d.type === 'income' ? 'income' : 'expense',
        recurrenceType: d.recurrenceType || 'monthly',
        startDate: parseDate(d.startDate) || new Date(),
      });
    }
  });

  let out = transactions.filter((t) => {
    if (!t.isRecurring) return true;
    // Deltas are stored, so a scenario saved before reference tokens stopped
    // counting toward identity still names its series by the old key.
    return !seriesKeyAliases(t).some((key) => removeKeys.has(key));
  });

  // Synthesize add-recurring "templates" as a single instance; buildProjection's
  // recurring engine uses lastInstance dates to drive projections, so one stub
  // is enough to seed the series. The stub sits one cycle BEFORE startDate:
  // the engine counts occurrences after the last instance, so a stub dated on
  // startDate itself dropped the first month of every added expense.
  adds.forEach((a, i) => {
    out = out.concat({
      id: `__scn_${scenario.id || 'tmp'}_${i}`,
      description: a.desc,
      amount: a.amount,
      type: a.type,
      isRecurring: true,
      recurrenceType: a.recurrenceType,
      date: previousOccurrence(a.startDate, a.recurrenceType).toISOString(),
      category: 'Other',
      __synthetic: true,
    });
  });

  return out;
}

// Collect the scaleRecurring deltas from the enabled scenarios, normalized for
// buildProjection's `scaleDeltas` option. startDate is kept as the raw string;
// the projection parses and compares it per projected month.
export function scaleDeltasOf(scenarios = []) {
  const out = [];
  scenarios
    .filter((s) => s && s.enabled && Array.isArray(s.deltas))
    .forEach((s) => {
      s.deltas.forEach((d) => {
        if (d && d.kind === 'scaleRecurring' && d.seriesKey && Number.isFinite(Number(d.multiplier))) {
          out.push({ seriesKey: d.seriesKey, multiplier: Number(d.multiplier), startDate: d.startDate || null });
        }
      });
    });
  return out;
}

export function applyScenarios(transactions, scenarios = []) {
  const enabled = scenarios.filter((s) => s && s.enabled);
  if (enabled.length === 0) return transactions;
  let out = transactions;
  enabled.forEach((s) => { out = applyScenarioDeltas(out, s); });
  return out;
}
